import DataTable from "react-data-table-component";
import { useEffect, useState } from "react";
import Button from "../../../../components/common/button";
import Switch from "../../../../components/common/Switch";
import AddTVOffTimerModal from "../../../../components/tv-app/sleep-concierge/add-tv-off-timer-modal";
import DeleteAndroidHome from "../../../../components/tv-app/delete-android-home";
import EditTableRowName from "../../../../components/tv-app/edit-name";
import DiscardSaveButtons from "../../../../components/tv-app/discard-save-buttons";
import {
  ISleepCategoryComponentProps,
  ITableColumn,
  ITVOffTimerSubData,
} from "../../../../utils/types";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { DndProvider } from "react-dnd";
import useUpdateAndroidTVHomeOrder from "../../../../hooks/use-update-android-home-order";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableRow from "../../../../components/common/DragRow";
import { DragSvg } from "../../../../icons";
import { setDragOnlyInStaging } from "../../../../utils/helpers";
import { useGetUserPermission } from "../../../../context/AuthStaffUserContext";

type IFormData = {
  showTOT: boolean;
  data: ITVOffTimerSubData[];
};

const TVOffTimer = ({
  subData = [],
  show,
  onSave,
  loading,
  categoryName,
  type,
}: ISleepCategoryComponentProps<ITVOffTimerSubData[]>) => {
  const getInitialFormData = () => {
    return {
      showTOT: !!show,
      data: subData.sort((a, b) => a?.orderKey - b?.orderKey) ?? [],
    };
  };

  const [InitialFormData, setInitialFormData] = useState<IFormData>(
    getInitialFormData()
  );

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const methods = useForm<IFormData>({ defaultValues: InitialFormData });
  const { reset, handleSubmit } = methods;

  const {
    fields: sleepCatagoryTOTFields,
    append: appendSleepCatagoryTOTField,
    remove: removeSleepCatagoryTOTField,
  } = useFieldArray({
    name: "data",
    control: methods.control,
    keyName: "customId",
  });

  const [tvOffTimerData, setTvOffTimerData] = useState(() => ({
    categoryName,
    items:
      [...(sleepCatagoryTOTFields as ITVOffTimerSubData[])]?.sort(
        (a, b) => a.orderKey - b.orderKey
      ) ?? [], // Use subData directly or default to an empty array
  }));

  const { mutate: updateAndroidTVHomeOrder } = useUpdateAndroidTVHomeOrder();

  useEffect(() => {
    // if (subData) setData(JSON.parse(JSON.stringify(subData)));

    if (sleepCatagoryTOTFields.length < subData.length) {
      const addedElement = subData[subData.length - 1];
      appendSleepCatagoryTOTField({ ...addedElement });
      setInitialFormData((pre) => ({
        ...pre,
        data: [...pre.data, { ...addedElement }],
      }));
    }
  }, [subData]);

  useEffect(() => {
    setTvOffTimerData((prev) => {
      const existingIds = new Set(prev.items.map((item) => item.id));
      const newItems = sleepCatagoryTOTFields.filter(
        (item) => item.id && !existingIds.has(item.id)
      );
      return {
        ...prev,
        items: [...prev.items, ...newItems].sort(
          (a, b) => a.orderKey - b.orderKey
        ),
      };
    });
  }, [sleepCatagoryTOTFields]);

  const getTableDataIndex = (id: string) => {
    return sleepCatagoryTOTFields.findIndex((item) => item.id === id);
  };
  const onDropComplete = () => {
    const updatedItems = [...tvOffTimerData.items].map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));
    setTvOffTimerData((prev) => ({
      ...prev,
      items: updatedItems.sort((a, b) => a?.orderKey - b?.orderKey),
    }));
    const updatedDataWithOrderKey = updatedItems.map((item) => ({
      id: item.id,
      orderKey: item.orderKey,
    }));
    updateAndroidTVHomeOrder({
      categoryName,
      type,
      data: updatedDataWithOrderKey,
    });
  };

  const onSubmit = (submitData: IFormData) => {
    const orderKeyMap = new Map(
      tvOffTimerData.items.map((item, index) => [
        item.id,
        item.orderKey || index + 1,
      ])
    );

    const mergedData = submitData.data
      .filter((item) => item?.id?.trim())
      .map((item) => ({
        ...item,
        orderKey: orderKeyMap.get(item.id) ?? item.orderKey,
      }));
    reset((prev) => ({
      ...prev,
      data: mergedData,
    }));
    onSave({
      show: submitData.showTOT,
      subData: mergedData.sort((a, b) => a?.orderKey - b?.orderKey),
    });
  };

  const { write: isEditPermission } = useGetUserPermission("tv-app");

  const tableColumns: ITableColumn<ITVOffTimerSubData>[] = setDragOnlyInStaging<
    ITableColumn<ITVOffTimerSubData>
  >([
    {
      name: "",
      cell: (Row, index) => (
        <DraggableRow
          index={index}
          moveRow={moveRow}
          onDropComplete={onDropComplete}
        >
          <DragSvg
            height={24}
            width={24}
            className="h-6 w-6 cursor-grab flex-none"
          />
        </DraggableRow>
      ),
      sortable: true,
      width: "10%",
    },
    {
      name: "Name",
      cell: (Row) => (
        <EditTableRowName
          name={`data.${getTableDataIndex(Row.id)}.name`}
          disabled={!isEditPermission}
        />
      ),
      sortable: true,
      width: "60%",
    },
    {
      name: "Hide/Show",
      cell: (Row) => (
        <Switch name={`data.${getTableDataIndex(Row.id)}.selected`} />
      ),
      sortable: true,
      center: true,
      width: "20%",
    },
    {
      name: "Action",
      cell: (Row) => (
        <DeleteAndroidHome
          categoryName={categoryName}
          type={type}
          deleteId={Row.id}
          disabled={!isEditPermission}
          removeField={() => {
            setInitialFormData((pre) => ({
              ...pre,
              data: pre.data.filter((item) => item.id !== Row.id),
            }));
            removeSleepCatagoryTOTField(getTableDataIndex(Row.id));
            setTvOffTimerData({
              ...tvOffTimerData,
              items: tvOffTimerData.items.filter((item) => item.id !== Row.id),
            });
          }}
        />
      ),
    },
  ]);

  const moveRow = (fromIndex: number, toIndex: number) => {
    if (!tvOffTimerData?.items) return;
    const globalFromIndex = (currentPage - 1) * pageSize + fromIndex;
    const globalToIndex = (currentPage - 1) * pageSize + toIndex;

    const updatedData = [...tvOffTimerData.items];
    const [movedItem] = updatedData.splice(globalFromIndex, 1);
    updatedData.splice(globalToIndex, 0, movedItem);

    const reOrdered = updatedData.map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));

    setTvOffTimerData({
      ...tvOffTimerData,
      items: reOrdered,
    });
  };

  return (
    <FormProvider {...methods}>
      <div className="w-full mt-4">
        {/* <Switch
          name="showTOT"
          label="TV Off Timer (Hide/Show)"
          // checked={showTOT}
          // onCheck={(checked) => setShowTOT(checked)}
        /> */}
        <div className="flex items-center justify-between w-full">
          <h4 className="text-base leading-[39px] font-normal text-[#1E2F65] whitespace-nowrap">
            TV Off Timer
          </h4>
          <Button
            variant="primary"
            className="whitespace-nowrap"
            onClick={openModal}
            disabled={!isEditPermission}
          >
            Add New
          </Button>
        </div>
        {/* <div className="h-full max-h-[calc(100vh-450px)] overflow-auto mt-[17px]"> */}
        <DndProvider backend={HTML5Backend}>
          <DataTable
            className="border-t mt-[17px]"
            data={tvOffTimerData.items}
            // @ts-ignore
            columns={tableColumns}
            striped={true}
            persistTableHead
            pagination={sleepCatagoryTOTFields.length > 10}
            customStyles={{
              headRow: { style: { paddingLeft: "36px" } },
              rows: { style: { paddingLeft: "36px" } },
            }}
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(perPage) => setPageSize(perPage)}
          />
        </DndProvider>
        {/* </div> */}

        <DiscardSaveButtons
          onDiscard={() => {
            reset(getInitialFormData());
          }}
          onSave={handleSubmit(onSubmit)}
          loading={loading}
          className="mt-10"
        />

        <AddTVOffTimerModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          categoryName={categoryName}
          type={type}
        />
      </div>
    </FormProvider>
  );
};

export default TVOffTimer;
